import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { Form, Button } from "react-bootstrap";
import "./styles/OESettingsComponent.css";
import CheckboxWithTooltip from "./CheckboxWithTooltip";
import MaxDiffConfigComponent from "./MaxDiffConfigComponent";
import { FormattableInput } from "./FormattableInput";
import QuotaConfigComponent from "./QuotaConfigComponent";
import { buildQuotaQuestion, parseQuotaQuestion } from "./quotaUtils";

// Label + info-icon + click-to-show tooltip, for dropdowns that replaced a
// group of individual CheckboxWithTooltip options (Mode, NumericGrid's
// Input Type) - so the per-option explanations those checkboxes used to
// carry aren't lost, just consolidated onto the dropdown's label instead of
// scattered across several now-hidden checkboxes. Same look/interaction as
// CheckboxWithTooltip's own info icon, minus the checkbox itself.
const LabelWithInfo = ({ label, tooltip }) => {
  const [show, setShow] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!show) return;
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setShow(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [show]);

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {label}
      <span
        ref={ref}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setShow((v) => !v); }}
        style={{ cursor: "pointer", display: "inline-flex", alignItems: "center", position: "relative" }}
      >
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{ color: show ? "#007bff" : "#6c757d", fontSize: 13, transition: "color 0.2s ease" }}
        />
        {show && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-10%)",
              marginTop: 8,
              padding: "10px 12px",
              backgroundColor: "#333",
              color: "#fff",
              borderRadius: 6,
              fontSize: 12,
              whiteSpace: "normal",
              width: "max-content",
              maxWidth: 320,
              zIndex: 1000,
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
              lineHeight: 1.5,
              fontWeight: 400,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {tooltip}
          </div>
        )}
      </span>
    </span>
  );
};

// Question `type` values come from several different sources (manual entry,
// imports, older saved surveys) that don't agree on casing/spacing -
// "SingleGrid", "Single Grid", "singlegrid" all mean the same thing. Every
// comparison against a canonical type name in this file goes through this
// first so all of them are treated alike.
const normalizeType = (type) => String(type || "").replace(/\s+/g, "").toLowerCase();
const typeMatches = (a, b) => normalizeType(a) === normalizeType(b);

// Question types the Common/Question-Type property spec actually covers.
// Any other qtype value (Intro, CustomRows, CustomCols, loop, loopend,
// SingleGridSlider, Single2DGrid, Numeric2DGrid, or none selected yet) falls
// outside the spec entirely, so every property stays visible for it rather
// than guessing at rules the spec never defined.
const RECOGNIZED_QTYPES = [
  "SingleResponse",
  "MultipleResponse",
  "String",
  "StringGrid",
  "Numeric",
  "NumericGrid",
  "SingleGrid",
  "MultiGrid",
];

// Maps an oeParameters key to the question types it's restricted to, per the
// Common/Question-Type property spec. A key that's absent from this map is
// unrestricted — shown for every question type — because it's either an
// explicitly Common property, or an existing property the spec doesn't
// mention at all (kept visible everywhere so nothing that already works
// disappears; see conversation history for that call).
const TYPE_SCOPED_PROPERTIES = {
  // Response-style toggles
  isAnswerButton: ["SingleResponse", "MultipleResponse", "SingleGrid", "MultiGrid"],
  isDropDown: ["SingleResponse", "MultipleResponse", "SingleGrid", "MultiGrid"],
  isSlider: ["SingleResponse", "SingleGrid"],
  isVerticalSlider: ["SingleResponse", "SingleGrid"],
  isHorizontalRatingScale: ["SingleResponse", "SingleGrid"],
  noOfColumns: ["SingleResponse", "MultipleResponse"],
  noOfRows: ["SingleResponse", "MultipleResponse"],
  isCarousel: ["SingleGrid", "MultiGrid"],
  isGridbars: ["SingleResponse", "SingleGrid"],
  isSequential: ["MultipleResponse", "SingleGrid", "MultiGrid", "StringGrid", "NumericGrid"],
  isCarouselGridbars: ["SingleGrid"],
  isCardSort: ["SingleGrid", "MultiGrid"],
  isAccordion: ["SingleGrid", "MultiGrid"],
  isAccordionAnswerButton: ["SingleGrid", "MultiGrid"],

  // Numeric formatting / text formatting
  isDecimal: ["Numeric", "NumericGrid"],
  // How many decimal digits a Numeric/NumericGrid answer may have - only
  // meaningful once Decimal is enabled. Same validation/behavior as the
  // row-level Allow Decimals + Number of Decimal Places in
  // RowColumnProperties.js's Numeric Grid Settings section.
  decimalPlaces: ["Numeric", "NumericGrid"],
  maxdigit: ["Numeric", "NumericGrid"],
  // Also reused as "Number of Characters" (Min/Max) for String - see the
  // dynamic labels on the Min/Max fields below.
  min: ["MultipleResponse", "MultiGrid", "Numeric", "NumericGrid", "String"],
  max: ["MultipleResponse", "MultiGrid", "Numeric", "NumericGrid", "String"],
  preText: ["String", "StringGrid", "Numeric", "NumericGrid"],
  postText: ["String", "StringGrid", "Numeric", "NumericGrid"],
  isNoanswer: ["String", "StringGrid", "Numeric", "NumericGrid"],
  noAnswerLabel: ["String", "StringGrid", "Numeric", "NumericGrid"],
  exact: ["MultipleResponse", "MultiGrid"],

  // Grid totals - NumericGrid only
  isSumTotal: ["NumericGrid"],
  isSum100: ["NumericGrid"],
  sum100Target: ["NumericGrid"],
  summin: ["NumericGrid"],
  summax: ["NumericGrid"],
  tbCorner: ["SingleGrid", "MultiGrid", "NumericGrid", "StringGrid"],
  nboxes: ["NumericGrid", "StringGrid"],
  nboxesrequired: ["NumericGrid", "StringGrid"],
  isFirstComp: ["NumericGrid", "StringGrid"],
  isDuplicate: ["StringGrid"],

  // Grid-only (own tooltip/label names the type)
  isRightLeft: ["SingleGrid"],
  isCol: ["SingleGrid", "MultiGrid"],
  disabledCells: ["SingleGrid", "MultiGrid", "NumericGrid", "StringGrid"],
  // Run each grid row as its own looped block/page instead of one grid.
  isLoop: ["SingleGrid", "MultiGrid", "NumericGrid", "StringGrid"],
  // Export the grid with Dimension's 'UnboundList' questionLook (open-ended
  // free-text boxes) instead of the standard grid layout - String Grid only.
  isUnbound: ["StringGrid"],

  // SingleResponse-only (own tooltip names the type)
  isSinCol: ["SingleResponse"],

  isTextarea: ["String", "StringGrid"],
  isDate: ["String", "StringGrid"],

  istablelook: ["Numeric"],

  isDragDrop: ["MultipleResponse"],

  isDrag: ["MultipleResponse"],
  isDragDropRank: ["MultipleResponse"],
  isClick: ["MultipleResponse", "SingleGrid", "MultiGrid"],
  isNumberRank: ["MultipleResponse", "SingleGrid", "MultiGrid"],

  inputType: ["String", "StringGrid"],
  isQuota: ["SingleResponse", "MultipleResponse"],
  prefill: ["String", "StringGrid", "Numeric", "NumericGrid",],
  // MaxDiff is a choice-exercise concept - doesn't apply to open-end
  // (String/StringGrid) or plain numeric-entry (Numeric/NumericGrid)
  // question types.
  isMaxDiff: ["SingleResponse", "MultipleResponse", "SingleGrid", "MultiGrid"],
  isWithoutMaxDiff: ["SingleResponse", "MultipleResponse", "SingleGrid", "MultiGrid"],
};

// Mode selectors: for SingleResponse and MultipleResponse, the response
// widget is a single mutually-exclusive choice rather than independent
// checkboxes - selecting one mode hides every other mode's property from
// the panel entirely (not just unchecks it). `flag` is the oeParameters
// boolean the mode maps to for backward compatibility with existing
// rendering/export logic; `null` means the "default" widget (no flag set).
// Types absent from this map (Grids, String, Numeric, ...) keep the classic
// independent checkboxes - modes were only specified for these two types.
const MODE_OPTIONS = {
  SingleResponse: [
    { value: "radioButton", label: "Radio Button", flag: null },
    { value: "answerButton", label: "Answer Button", flag: "isAnswerButton" },
    { value: "dropDown", label: "Drop Down", flag: "isDropDown" },
    { value: "gridbars", label: "Gridbars", flag: "isGridbars" },
    { value: "verticalSlider", label: "Vertical Slider", flag: "isVerticalSlider" },
    { value: "horizontalSlider", label: "Horizontal Slider", flag: "isSlider" },
    { value: "horizontalRatingScale", label: "Horizontal Rating Scale", flag: "isHorizontalRatingScale" },
  ],
  MultipleResponse: [
    { value: "checkBox", label: "Check Box", flag: null },
    { value: "answerButton", label: "Answer Button", flag: "isAnswerButton" },
    { value: "dropDown", label: "Drop Down", flag: "isDropDown" },
    // Ranking modes (Qualtrics-style) - same ranking functionality either
    // way, only the interaction method differs, so they're just more
    // mutually-exclusive Mode options rather than a separate checkbox group.
    { value: "dragDrop", label: "Drag & Drop", flag: "isDragDrop" },

    { value: "drag", label: "Rank - Drag (Reorder)", flag: "isDrag" },
    { value: "dragDropRank", label: "Rank - Drag & Drop", flag: "isDragDropRank" },
    { value: "clickToRank", label: "Rank - Click to Rank", flag: "isClick" },
    { value: "numberToRank", label: "Rank - Number to Rank", flag: "isNumberRank" },
  ],
  SingleGrid: [
    { value: "radioButton", label: "Radio Button", flag: null },
    { value: "answerButton", label: "Answer Button", flag: "isAnswerButton" },
    { value: "dropDown", label: "Drop Down", flag: "isDropDown" },
    { value: "gridbars", label: "Gridbars", flag: "isGridbars" },
    { value: "slider", label: "Slider", flag: "isSlider" },
    { value: "cardSort", label: "CardSort", flag: "isCardSort" },
    { value: "accordion", label: "Accordion", flag: "isAccordion" },
    { value: "accordionAnswerButton", label: "Accordion Answer Button", flag: "isAccordionAnswerButton" },
    { value: "carousel", label: "Carousel", flag: "isCarousel" },
    { value: "carouselGridbars", label: "Carousel Gridbars", flag: "isCarouselGridbars" },
  ],
  MultiGrid: [
    { value: "checkBox", label: "Check Box", flag: null },
    { value: "answerButton", label: "Answer Button", flag: "isAnswerButton" },
    { value: "dropDown", label: "Drop Down", flag: "isDropDown" },
    { value: "cardSort", label: "CardSort", flag: "isCardSort" },
    { value: "accordion", label: "Accordion", flag: "isAccordion" },
    { value: "accordionAnswerButton", label: "Accordion Answer Button", flag: "isAccordionAnswerButton" },
    { value: "carousel", label: "Carousel", flag: "isCarousel" },
  ],
};

// Every oeParameters flag any mode above can set - cleared whenever a new
// mode is chosen so only the selected mode's flag ends up true.
const MODE_FLAG_KEYS = [
  "isAnswerButton",
  "isDropDown",
  "isVerticalSlider",
  "isSlider",
  "isHorizontalRatingScale",
  "isDrag",
  "isDragDrop",
  "isDragDropRank",
  "isClick",
  "isNumberRank",
  "isGridbars",
  "isCardSort",
  "isAccordion",
  "isAccordionAnswerButton",
  "isCarousel",
  "isCarouselGridbars",
];

const OESettingsComponent = ({
  oeParameters,
  label,
  setOeParameters,
  qtype,
  savePropertyChanges,
  selectedVariable,
  onQuotaUpsert,
  onQuotaRemove,
  existingQuotaQuestion,
  availableQuotas = [],
  onAssignQuota,
  onOpenQuotaModal,
}) => {
  console.log("OE selectedVariable:", selectedVariable);
  console.log("OE rows:", selectedVariable?.rows);
  const [showMaxDiffModal, setShowMaxDiffModal] = React.useState(false);
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const [maxDiffDesign, setMaxDiffDesign] = useState({
    method: "create",
    itemsPerSet: 5,
    tasksPerRespondent: 10,
    designVersions: 3,
    designData: "",
    designType: "balanced"
  });
  const [showQuotaSelectPopup, setShowQuotaSelectPopup] = useState(false);

  // Single2DGrid/Numeric2DGrid reuse their 1D counterpart's entire OE
  // Parameters setup - same rows-of-answer-options shape, just with an
  // extra column dimension, so there's no separate property spec for them.
  // Resolved once here so every qtype-based lookup below (isPropVisible,
  // Mode, RECOGNIZED_QTYPES, the Intro/NumericGrid special-cases) treats a
  // 2D grid exactly like its 1D counterpart without needing its own rules.
  const GRID_2D_ALIASES = { Single2DGrid: "SingleGrid", Numeric2DGrid: "NumericGrid" };
  const aliasKey = Object.keys(GRID_2D_ALIASES).find((k) => typeMatches(k, qtype));
  const effectiveQtype = aliasKey ? GRID_2D_ALIASES[aliasKey] : qtype;

  // Common properties (absent from TYPE_SCOPED_PROPERTIES) are always
  // visible. Type-scoped properties only show up for the qtypes listed
  // against them - unless qtype itself falls outside the spec, in which
  // case nothing is hidden.
  const isPropVisible = (key) => {
    const allowedTypes = TYPE_SCOPED_PROPERTIES[key];
    if (!allowedTypes) return true;
    if (!RECOGNIZED_QTYPES.some((t) => typeMatches(t, effectiveQtype))) return true;
    return allowedTypes.some((t) => typeMatches(t, effectiveQtype));
  };

  // Modes available for the current qtype, or undefined for types that use
  // the classic independent checkboxes instead of a Mode selector.
  const modeOptionsKey = Object.keys(MODE_OPTIONS).find((k) => typeMatches(k, effectiveQtype));
  const modeOptions = modeOptionsKey ? MODE_OPTIONS[modeOptionsKey] : undefined;

  // Different mode-enabled types absorb different properties into their
  // Mode dropdown (e.g. MultipleResponse's Mode owns the ranking flags,
  // but SingleGrid/MultiGrid's Mode doesn't) - so "does this qtype have ANY
  // Mode selector" isn't precise enough to decide whether one specific
  // classic checkbox should hide. This checks whether the CURRENT type's
  // Mode list specifically claimed a given flag.
  const modeFlagsForType = new Set((modeOptions || []).map((o) => o.flag).filter(Boolean));
  const isModeOwned = (flag) => modeFlagsForType.has(flag);

  // The ranking group (Drag/Drag&Drop/Click/Number-Rank) shares one allowed-
  // types list, so any one of them tells us whether the whole box has
  // anything to show - avoids rendering an empty bordered wrapper. For
  // MultipleResponse these four are now Mode options instead (see
  // MODE_OPTIONS above), so the classic checkbox group only still applies
  // to SingleGrid/MultiGrid, which don't put ranking in their Mode list.
  const showRankingGroup = isPropVisible('isDrag') && !isModeOwned('isDrag');

  // Whether the "specific to this type" block in each checkbox column has
  // anything to show - some types (e.g. NumericGrid) have no type-specific
  // properties left in these particular columns, so the divider label is
  // skipped rather than floating over an empty block.
  const columnASpecificVisible =
    ['isNoanswer', 'isRightLeft', 'isCol', 'isSinCol', 'isLoop', 'isUnbound'].some(isPropVisible) ||
    (isPropVisible('isCarousel') && !isModeOwned('isCarousel')) ||
    (isPropVisible('isGridbars') && !isModeOwned('isGridbars')) ||
    (isPropVisible('isCardSort') && !isModeOwned('isCardSort')) ||
    (isPropVisible('isAccordion') && !isModeOwned('isAccordion')) ||
    (isPropVisible('isSlider') && !isModeOwned('isSlider')) ||
    (isPropVisible('isVerticalSlider') && !isModeOwned('isVerticalSlider')) ||
    (isPropVisible('isHorizontalRatingScale') && !isModeOwned('isHorizontalRatingScale')) ||
    (isPropVisible('isAnswerButton') && !isModeOwned('isAnswerButton'));

  const columnBSpecificVisible = showRankingGroup || isPropVisible('istablelook');

  // Textarea/Date Picker render inline next to the Input Type dropdown
  // instead of as unrelated standalone checkboxes - Textarea goes with
  // Input Type "text", Date Picker with "date". The already-true fallback
  // keeps a legacy record's checkbox visible even if inputType ended up on
  // a different value, so a previously-enabled option never silently
  // disappears from the UI while still being applied under the hood.
  const inputTypeVal = oeParameters?.inputType || "text";
  const showTextareaOption = isPropVisible('isTextarea') && (inputTypeVal === "text" || oeParameters?.isTextarea);
  const showDateOption = isPropVisible('isDate') && (inputTypeVal === "date" || oeParameters?.isDate);

  const validateSpecificVisible =
    isPropVisible('isFirstComp') || isPropVisible('isDuplicate') || isPropVisible('disabledCells');

  // String reuses the Min/Max fields as character-count limits ("Number of
  // Characters") instead of Numeric's plain min/max value range - same
  // fields, different label/meaning depending on the current type.
  const isStringType = typeMatches(effectiveQtype, 'String');

  // The active mode: explicit oeParameters.responseMode if already set,
  // otherwise derived from whichever legacy boolean flag is already true -
  // so questions saved before the Mode selector existed still show the
  // right selection instead of silently resetting to the default widget.
  const getCurrentMode = () => {
    if (!modeOptions) return null;
    if (oeParameters?.responseMode) return oeParameters.responseMode;
    const active = modeOptions.find((o) => o.flag && oeParameters?.[o.flag]);
    return active ? active.value : modeOptions[0].value;
  };

  // Selecting a mode is mutually exclusive: clear every flag any mode for
  // this qtype could set, then set only the chosen mode's flag (if it has
  // one - "Radio Button"/"Check Box" are the flagless defaults).
  const handleModeChange = (modeValue) => {
    const newParams = { ...oeParameters, responseMode: modeValue };
    MODE_FLAG_KEYS.forEach((flagKey) => {
      newParams[flagKey] = false;
    });
    const selected = modeOptions?.find((o) => o.value === modeValue);
    if (selected?.flag) newParams[selected.flag] = true;
    savePropertyChanges(newParams, "oeParameters");
  };

  // NumericGrid's Input Type: mutually-exclusive Numeric/Sum Total/Constant
  // Sum, replacing the old independent Sum Total + Sum 100 checkboxes -
  // same backward-compatible-derivation pattern as Mode above, mapping back
  // onto the same isSumTotal/isSum100 flags so nothing downstream that
  // already reads those flags needs to change.
  const getCurrentNumericGridInputType = () => {
    if (oeParameters?.isSumTotal) return "sumTotal";
    if (oeParameters?.isSum100) return "constantSum";
    return "numeric";
  };

  const handleNumericGridInputTypeChange = (value) => {
    const newParams = {
      ...oeParameters,
      isSumTotal: value === "sumTotal",
      isSum100: value === "constantSum",
    };
    savePropertyChanges(newParams, "oeParameters");
  };

  useEffect(() => {
    if (oeParameters?.isRequired === undefined) {
      const newParams = { ...(oeParameters || {}), isRequired: true };
      savePropertyChanges(newParams, "oeParameters");
    }
  }, [selectedVariable?.Question_number]);

  // Handler for all changes - directly update and save
  const handleChange = (property, value) => {
    const newParams = {
      ...oeParameters,
      [property]: value
    };

    // Handle exclusive checkboxes
    const exclusiveRankingOptions = ['isDropDown', 'isClick', 'isNumberRank'];

    if (exclusiveRankingOptions.includes(property) && value === true) {
      // Uncheck other ranking options
      exclusiveRankingOptions.forEach(opt => {
        if (opt !== property) {
          newParams[opt] = false;
        }
      });
    }

    // Handle other exclusivity rules
    if (property === 'isDrag' && value === true) {
      newParams.isSlider = false;
    } else if (property === 'isSlider' && value === true) {
      newParams.isDrag = false;
    }

    savePropertyChanges(newParams, "oeParameters");
  };

  // Special handler for MaxDiff
  const handleMaxDiffChange = (e) => {
    const isChecked = e.target.checked;
    handleChange('isMaxDiff', isChecked);
    if (isChecked) {
      setShowMaxDiffModal(true);
    }
  };

  // Handler for MaxDiff configuration updates
  const handleMaxDiffConfigUpdate = (config) => {
    const newParams = {
      ...oeParameters,
      ...config
    };
    setOeParameters(newParams);
    savePropertyChanges(newParams, "oeParameters");
  };

  // Special handler for Quota: flag the source question and open the config
  // modal. Unchecking deletes the generated hidden quota question.
  // OESettingsComponent.jsx

  const handleQuotaSave = (config) => {
    const newParams = {
      ...oeParameters,
      quotaConfig: config.cells  // [{ index, count }]
    };
    savePropertyChanges(newParams, "oeParameters");
  };

  // Helper — assigned quota list se compute karo
  const getAssignedList = () => {
    const assignedRaw = selectedVariable?.assignedQuota || "";
    if (Array.isArray(assignedRaw)) return assignedRaw;
    return assignedRaw.split(",").map(s => s.trim()).filter(Boolean);
  };

  // Popup close karte waqt — agar koi quota assigned nahi toh isQuota false karo
  const closePopupSafely = () => {
    const assignedList = getAssignedList();
    if (assignedList.length === 0) {
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
    }
    setShowQuotaSelectPopup(false);
  };

  const handleQuotaCheckboxChange = () => {
    if (oeParameters?.isQuota) {
      // Already true tha — uncheck kiya, sab clear karo
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
      onAssignQuota?.("");
    } else {
      // False tha — check kiya, sirf popup kholo, isQuota abhi true mat karo
      setShowQuotaSelectPopup(true);
    }
  };

  const handleDoneClick = () => {
    const assignedList = getAssignedList();
    if (assignedList.length > 0) {
      // Quota selected hai — isQuota true karo
      handleChange('isQuota', true);
    } else {
      // Koi quota select nahi kiya — checkbox uncheck rakho
      const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
      savePropertyChanges(newParams, "oeParameters");
    }
    setShowQuotaSelectPopup(false);
  };

  const handleRemoveAll = () => {
    const newParams = { ...oeParameters, isQuota: false, quotaConfig: [] };
    savePropertyChanges(newParams, "oeParameters");
    onAssignQuota?.("");
    setShowQuotaSelectPopup(false);
  };

  const toggleQuotaSelection = (quotaName) => {
    const assignedList = getAssignedList();
    const isSelected = assignedList.includes(quotaName);
    const newList = isSelected
      ? assignedList.filter(n => n !== quotaName)
      : [...assignedList, quotaName];
    onAssignQuota?.(newList.join(","));
  };

  // Intro questions carry no answer options, so almost nothing in this
  // panel applies to them - only Display on Same Page and Termination
  // Condition do. Rather than threading an Intro exception through every
  // section below (Settings/Advanced Settings/Validate boxes, Quota,
  // MaxDiff...), render just those two directly and skip the rest of the
  // panel entirely. loop/loopend and CustomRows/CustomCols intentionally
  // stay fully open (no scoping) - unlike Intro they're left unrestricted.
  if (typeMatches(qtype, "Intro")) {
    return (
      <div className="oe-settings-section">
        <div className="oe-header">
          <h4>{label}</h4>
          {selectedVariable && (
            <div className="oe-variable-info">
              <small className="text-muted">
                Source: {selectedVariable.Question_number || selectedVariable.name}
                {selectedVariable.type && ` (${selectedVariable.type})`}
              </small>
            </div>
          )}
        </div>
        <div className="oe-main-content">
          <div className="oe-settings-box">
            <h5>General Settings</h5>
            <CheckboxWithTooltip
              checked={oeParameters?.isSamePage || false}
              onChange={(e) => handleChange('isSamePage', e.target.checked)}
              label="Display on Same Page"
              tooltip="Keep this question on the same page as the immediately preceding question. To group 3+ questions together, enable this on each consecutive question in the group."
            />
            <Form.Group className="oe-form-label-margin-bottom" style={{ marginTop: 12 }}>
              <Form.Label className="oe-form-label">Termination Condition</Form.Label>
              <FormattableInput
                value={oeParameters?.term || ""}
                onChange={(e) => handleChange('term', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Enter condition"
              />
            </Form.Group>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="oe-settings-section">
      {/* Header */}
      <div className="oe-header">
        <h4>{label}</h4>
        {/* {JSON.stringify(selectedVariable)}; */}
        {/* print(selectedVariable); */}
        {selectedVariable && (
          <div className="oe-variable-info">
            <small className="text-muted">
              Source: {selectedVariable.Question_number || selectedVariable.name}
              {selectedVariable.type && ` (${selectedVariable.type})`}
            </small>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="oe-main-content">
        {/* Settings Box */}
        <div className="oe-settings-box">
          <h5>General Settings</h5>

          {/* Checkboxes Row */}
          <div className="oe-input-checkboxes">
            <CheckboxWithTooltip
              checked={oeParameters?.isRequired === true}
              onChange={(e) => handleChange('isRequired', e.target.checked)}
              label="Required"
              tooltip="Make this field mandatory - respondents must provide an answer"
            />
            <CheckboxWithTooltip
              checked={oeParameters?.isHidden || false}
              onChange={(e) => handleChange('isHidden', e.target.checked)}
              label="Hidden Question"
              tooltip="Make a Question Hidden"
            />
            {isPropVisible('isQuota') && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CheckboxWithTooltip
                  checked={oeParameters?.isQuota || false}
                  onChange={handleQuotaCheckboxChange}
                  label="Quota Question"
                  tooltip="Make Question as Quota"
                />
                {oeParameters?.isQuota && (
                  <button
                    onClick={() => setShowQuotaSelectPopup(true)}
                    style={{
                      background: 'none',
                      border: '1px solid #c4b5fd',
                      color: '#7c3aed',
                      borderRadius: 5,
                      padding: '2px 8px',
                      fontSize: 11,
                      cursor: 'pointer',
                      lineHeight: 1.4,
                    }}
                  >
                    Edit
                  </button>
                )}
                {/* Assigned quota summary */}
                {oeParameters?.isQuota && getAssignedList().length > 0 && (
                  <div style={{
                    marginTop: 8,
                    padding: '6px 10px',
                    background: '#f0f0ff',
                    borderRadius: 6,
                    border: '1px solid #c4b5fd',
                    fontSize: 12,
                    color: '#4338ca',
                  }}>
                    <strong>Assigned:</strong> {getAssignedList().join(", ")}
                  </div>
                )}

                {oeParameters?.isQuota && existingQuotaQuestion?.rows?.length > 0 && (
                  <div className="quota-summary mt-2 p-2 bg-light rounded">
                    <small>
                      <strong>Quota ({existingQuotaQuestion.rows.length}):</strong><br />
                      {existingQuotaQuestion.rows.map((r) => (
                        <span key={r.index}>
                          {r.text}: {r.punchLogic} → {r.quotaCounts}<br />
                        </span>
                      ))}
                    </small>
                  </div>
                )}

                {/* Quota Select Popup */}
                {showQuotaSelectPopup && (
                  <div className="quota-select-overlay" onClick={closePopupSafely}>
                    <div className="quota-select-modal" onClick={(e) => e.stopPropagation()}>
                      {/* Header */}
                      <div className="quota-select-header">
                        <h5 className="quota-select-title">Select Quota(s) for this Question</h5>
                        <button onClick={closePopupSafely} className="quota-select-close-btn">×</button>
                      </div>

                      <div className="quota-select-body">
                        {availableQuotas.length === 0 ? (
                          <div className="quota-select-empty">
                            <p className="quota-select-warning-text">⚠ No quotas configured yet.</p>
                            {onOpenQuotaModal && (
                              <button
                                onClick={() => { setShowQuotaSelectPopup(false); onOpenQuotaModal(); }}
                                className="quota-select-create-first-btn"
                              >
                                + Create Quota First
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <p className="quota-select-hint">
                              Choose which quota(s) apply to this question (multiple allowed):
                            </p>

                            <div className="quota-select-list">
                              {availableQuotas.map((q) => {
                                const assignedList = getAssignedList();
                                const isSelected = assignedList.includes(q.quotaName);

                                return (
                                  <div
                                    key={q.quotaName}
                                    onClick={() => toggleQuotaSelection(q.quotaName)}
                                    className={`quota-select-item ${isSelected ? "selected" : ""}`}
                                  >
                                    <div className="quota-select-item-content">
                                      <div className="quota-select-item-header">
                                        <div className={`quota-select-checkbox ${isSelected ? "checked" : ""}`}>
                                          {isSelected && <span className="quota-select-checkbox-icon">✓</span>}
                                        </div>
                                        <span className="quota-select-item-name">{q.quotaName}</span>
                                      </div>
                                      {q.rows?.length > 0 && (
                                        <div className="quota-select-item-preview">
                                          {q.rows.slice(0, 3).map(r => r.name).join(' · ')}
                                          {q.rows.length > 3 && ` · +${q.rows.length - 3} more`}
                                        </div>
                                      )}
                                    </div>
                                    <span className="quota-select-item-badge">
                                      {q.rows?.length || 0} rows
                                    </span>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Create new quota */}
                            <div
                              className="quota-select-create-new"
                              onClick={() => { setShowQuotaSelectPopup(false); onOpenQuotaModal?.(); }}
                            >
                              <span className="quota-select-create-new-text">+ Create New Quota</span>
                            </div>
                          </>
                        )}
                      </div>

                      {/* Footer */}
                      {availableQuotas.length > 0 && (
                        <div className="quota-select-footer">
                          {getAssignedList().length > 0 ? (
                            <button onClick={handleRemoveAll} className="quota-select-remove-all-btn">
                              Remove All
                            </button>
                          ) : (
                            <div />
                          )}

                          <button onClick={handleDoneClick} className="quota-select-done-btn">
                            Save
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            {isPropVisible('isDecimal') && (
              <CheckboxWithTooltip
                checked={oeParameters?.isDecimal || false}
                onChange={(e) => handleChange('isDecimal', e.target.checked)}
                label="Allow Decimals"
                tooltip="It will change the format from number to decimals"
              />
            )}

            {isPropVisible('decimalPlaces') && oeParameters?.isDecimal && (
              <Form.Group className="oe-inline-number-field">
                <Form.Label className="oe-form-label">Number of Decimal Places Allowed</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={oeParameters?.decimalPlaces ?? ""}
                  onChange={(e) => handleChange('decimalPlaces', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="e.g. 2"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

          </div>

          {/* Input Fields */}
          <div className="oe-input-grid">


            {/* Mode: mutually-exclusive response widget for SingleResponse /
                MultipleResponse / SingleGrid / MultiGrid. Selecting a mode
                determines which mode-specific property (Answer Button, Drop
                Down, Slider, Carousel...) is active - the others aren't just
                unchecked, they're not shown at all (see the per-checkbox
                isModeOwned guards below). Rendered in place of Input Type
                since the two never apply to the same Question Type. */}
            {modeOptions && (
              <Form.Group>
                <Form.Label className="oe-form-label">
                  <LabelWithInfo
                    label="Mode"
                    tooltip="Chooses how respondents interact with this question's answer options - e.g. Answer Buttons, Drop Down, Slider, Carousel, Accordion, CardSort, or ranking (Drag/Click/Number to Rank). The choices shown depend on this question's type; only one mode is active at a time."
                  />
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={getCurrentMode()}
                  onChange={(e) => handleModeChange(e.target.value)}
                  className="oe-form-control-sm"
                >
                  {modeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {/* Vertical Slider renders pinned to the left of the screen by
                default (matches current behavior) - this only offers moving
                it to Center or Right instead. Only meaningful once Vertical
                Slider is the active Mode. */}
            {isPropVisible('isVerticalSlider') && getCurrentMode() === 'verticalSlider' && (
              <Form.Group>
                <Form.Label className="oe-form-label">
                  <LabelWithInfo
                    label="Slider Position"
                    tooltip="Where the vertical slider renders on the screen. Defaults to Left."
                  />
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={oeParameters?.sliderPosition || "left"}
                  onChange={(e) => handleChange('sliderPosition', e.target.value)}
                  className="oe-form-control-sm"
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                  <option value="right">Right</option>
                </Form.Select>
              </Form.Group>
            )}

            {isPropVisible('inputType') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Input Type</Form.Label>
                <Form.Select
                  size="sm"
                  value={oeParameters?.inputType || "text"}
                  onChange={(e) => handleChange('inputType', e.target.value)}
                  className="oe-form-control-sm"
                >
                  <option value="text">Text</option>
                  <option value="email">Email</option>
                  <option value="password">Password</option>
                  <option value="tel">Mobile Number</option>
                  <option value="url">URL</option>
                  <option value="date">Date</option>
                </Form.Select>

                {showTextareaOption && (
                  <div className="oe-inputtype-suboptions">
                    <CheckboxWithTooltip
                      checked={oeParameters?.isTextarea || false}
                      onChange={(e) => handleChange('isTextarea', e.target.checked)}
                      label="Textarea"
                      tooltip="Use a multi-line text area instead of single-line text input"
                    />
                  </div>
                )}

                {showDateOption && (
                  <div className="oe-inputtype-suboptions">
                    <CheckboxWithTooltip
                      checked={oeParameters?.isDate || false}
                      onChange={(e) => handleChange('isDate', e.target.checked)}
                      label="Date & Time Picker"
                      tooltip="Display a calendar date picker for date selection"
                    />
                    {oeParameters?.isDate && (
                      <div className="oe-datepicker-suboptions" style={{ marginLeft: 20 }}>
                        <CheckboxWithTooltip
                          checked={oeParameters?.past || false}
                          onChange={(e) => handleChange('past', e.target.checked)}
                          label="Past"
                          tooltip="Allow selecting past dates"
                        />
                        <CheckboxWithTooltip
                          checked={oeParameters?.present || false}
                          onChange={(e) => handleChange('present', e.target.checked)}
                          label="Present"
                          tooltip="Allow selecting the present date"
                        />
                        <CheckboxWithTooltip
                          checked={oeParameters?.future || false}
                          onChange={(e) => handleChange('future', e.target.checked)}
                          label="Future"
                          tooltip="Allow selecting future dates"
                        />
                      </div>
                    )}
                  </div>
                )}
              </Form.Group>
            )}

            {isPropVisible('preText') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Prefix (Pre-text)</Form.Label>
                <FormattableInput
                  value={oeParameters?.preText || ""}
                  onChange={(e) => handleChange('preText', e.target.value)}
                  type="contentEditable"
                  className="oe-formattable-input"
                  placeholder="Text before input"
                />
              </Form.Group>
            )}

            {isPropVisible('postText') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Suffix (Post-text)</Form.Label>
                <FormattableInput
                  value={oeParameters?.postText || ""}
                  onChange={(e) => handleChange('postText', e.target.value)}
                  type="contentEditable"
                  className="oe-formattable-input"
                  placeholder="Text after input"
                />
              </Form.Group>
            )}
          </div>

          <div className="oe-numeric-grid">
            <Form.Group>
              <Form.Label className="oe-form-label">
                Termination Condition
              </Form.Label>
              <FormattableInput
                value={oeParameters?.term || ""}
                onChange={(e) => handleChange('term', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Enter condition"
              />
            </Form.Group>

            {isPropVisible('exact') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Exact Selection</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.exact || ""}
                  onChange={(e) => handleChange('exact', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Exact Selection"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {/* NumericGrid Input Type: Numeric / Sum Total / Constant Sum -
                replaces the old independent Sum Total + Sum 100 checkboxes.
                Total Label Text (tbCorner) only applies for Sum Total,
                Target Value (sum100Target) only for Constant Sum. */}
            {isPropVisible('isSumTotal') && (
              <Form.Group>
                <Form.Label className="oe-form-label">
                  <LabelWithInfo
                    label="Input Type"
                    tooltip="Numeric: standard numeric input for each row. Sum Total: display and validate the total sum of all numeric inputs, with a Total Label Text shown at the grid's total row. Constant Sum: require all numeric inputs to add up to exactly a Target Value you set."
                  />
                </Form.Label>
                <Form.Select
                  size="sm"
                  value={getCurrentNumericGridInputType()}
                  onChange={(e) => handleNumericGridInputTypeChange(e.target.value)}
                  className="oe-form-control-sm"
                >
                  <option value="numeric">Numeric</option>
                  <option value="sumTotal">Sum Total</option>
                  <option value="constantSum">Constant Sum</option>
                </Form.Select>
              </Form.Group>
            )}

            {isPropVisible('isSum100') && getCurrentNumericGridInputType() === "constantSum" && (
              <Form.Group className="oe-inline-number-field">
                <Form.Label className="oe-form-label">Target Value</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.sum100Target ?? 100}
                  onChange={(e) => handleChange('sum100Target', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="100"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('maxdigit') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Total Digits</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.maxdigit || ""}
                  onChange={(e) => handleChange('maxdigit', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="No of digits in box"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('min') && (
              <Form.Group>
                <Form.Label className="oe-form-label">
                  {isStringType ? "Number of Characters - Minimum" : "Minimum Value"}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={oeParameters?.min ?? ""}
                  onChange={(e) => handleChange('min', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder={isStringType ? "Min characters" : "Min"}
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('max') && (
              <Form.Group>
                <Form.Label className="oe-form-label">
                  {isStringType ? "Number of Characters - Maximum" : "Maximum Value"}
                </Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={oeParameters?.max || ""}
                  onChange={(e) => handleChange('max', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder={isStringType ? "Max characters" : "Max"}
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {/* Total Sum Min/Max only mean anything once there's an actual
                total being validated - Sum Total's running total, or
                Constant Sum's required target. Plain Numeric mode has
                neither, so it's excluded here (previously shown for every
                NumericGrid regardless of mode). */}
            {isPropVisible('summin') && getCurrentNumericGridInputType() !== "numeric" && (
              <Form.Group>
                <Form.Label className="oe-form-label">Total Sum min Value</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.summin || ""}
                  onChange={(e) => handleChange('summin', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Total Sum Min"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('summax') && getCurrentNumericGridInputType() !== "numeric" && (
              <Form.Group>
                <Form.Label className="oe-form-label">Total Sum Max Value</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.summax || ""}
                  onChange={(e) => handleChange('summax', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Total Sum Max"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('isDate') && oeParameters?.isDate && (
              <Form.Group>
                <CheckboxWithTooltip
                  checked={oeParameters?.isDateRange || false}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    const newParams = { ...oeParameters, isDateRange: checked };
                    if (!checked) {
                      newParams.min_date = "";
                      newParams.max_date = "";
                    }
                    savePropertyChanges(newParams, "oeParameters");
                  }}
                  label="Set Date Range"
                  tooltip="Restrict selectable dates to a Min/Max range"
                />
              </Form.Group>
            )}

            {isPropVisible('isDate') && oeParameters?.isDate && oeParameters?.isDateRange && (
              <>
                <Form.Group>
                  <Form.Label className="oe-form-label">Min Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={oeParameters?.min_date || ""}
                    onChange={(e) => handleChange('min_date', e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="oe-form-control-sm"
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label className="oe-form-label">Max Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={oeParameters?.max_date || ""}
                    onChange={(e) => handleChange('max_date', e.target.value)}
                    placeholder="YYYY-MM-DD"
                    className="oe-form-control-sm"
                  />
                </Form.Group>
              </>
            )}
            <Form.Group>
              <Form.Label className="oe-form-label">Input Size</Form.Label>
              <Form.Control
                type="number"
                value={oeParameters?.size || ""}
                onChange={(e) => handleChange('size', e.target.value === "" ? "" : Number(e.target.value))}
                placeholder="Input size"
                className="oe-form-control-sm"
              />
            </Form.Group>

            {/* For NumericGrid specifically, Total Label Text only makes
                sense once Input Type = Sum Total - other grid types
                (SingleGrid/MultiGrid/StringGrid) don't have that dropdown
                at all, so they keep showing this unconditionally. */}
            {isPropVisible('tbCorner') &&
              (!typeMatches(effectiveQtype, 'NumericGrid') || getCurrentNumericGridInputType() === 'sumTotal') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Total Label (Table Corner Text)</Form.Label>
                <FormattableInput
                  value={oeParameters?.tbCorner || ""}
                  onChange={(e) => handleChange('tbCorner', e.target.value)}
                  type="contentEditable"
                  className="oe-formattable-input"
                  placeholder="Text to show in table corner"
                />
              </Form.Group>
            )}

            {isPropVisible('prefill') && (
              <Form.Group>
                <Form.Label className="oe-form-label">Prefill</Form.Label>
                {/* String/StringGrid prefill with an actual alphanumeric
                    default answer (e.g. "N/A") - Numeric/NumericGrid keep
                    the numeric-only input as before. */}
                {isPropVisible('inputType') ? (
                  <Form.Control
                    type="text"
                    value={oeParameters?.prefill || ""}
                    onChange={(e) => handleChange('prefill', e.target.value)}
                    placeholder="Prefill value"
                    className="oe-form-control-sm"
                  />
                ) : (
                  <Form.Control
                    type="number"
                    value={oeParameters?.prefill || ""}
                    onChange={(e) => handleChange('prefill', e.target.value === "" ? "" : Number(e.target.value))}
                    placeholder="Prefill value"
                    className="oe-form-control-sm"
                  />
                )}
              </Form.Group>
            )}

            {isPropVisible('nboxes') && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Input Box (Number of Boxes)</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.nboxes || ""}
                  onChange={(e) => handleChange('nboxes', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of OE boxes"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('noOfColumns') && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">No of Columns</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={oeParameters?.noOfColumns ?? ""}
                  onChange={(e) => handleChange('noOfColumns', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of columns"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {isPropVisible('noOfRows') && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">No. of rows</Form.Label>
                <Form.Control
                  type="number"
                  min={0}
                  value={oeParameters?.noOfRows ?? ""}
                  onChange={(e) => handleChange('noOfRows', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of rows"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}
            {/* 
            {isPropVisible('isSorting') && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">&nbsp;</Form.Label>
                <CheckboxWithTooltip
                  checked={oeParameters?.isSorting || false}
                  onChange={(e) => handleChange('isSorting', e.target.checked)}
                  label="Sorting"
                  tooltip="Allow rows/columns to be sorted for this question"
                />
              </Form.Group>
            )} */}

          </div>
        </div>

        {/* General Settings and Validate - Side by Side */}
        <div className="oe-two-column-grid">
          {/* General Settings */}
          <div className="oe-settings-box oe-general-settings">
            <h5>Advanced Settings</h5>

            {isPropVisible('nboxesrequired') && oeParameters?.nboxes > 0 && (
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Compulsory no of boxes</Form.Label>
                <Form.Control
                  type="number"
                  value={oeParameters?.nboxesrequired || ""}
                  onChange={(e) => handleChange('nboxesrequired', e.target.value === "" ? "" : Number(e.target.value))}
                  placeholder="Number of OE boxes to be filled compulsory"
                  className="oe-form-control-sm"
                />
              </Form.Group>
            )}

            {/* Checkboxes in two columns - Common properties always come
                first in each column; anything specific to the current
                Question Type follows below a divider, so the layout doesn't
                reshuffle unpredictably as you switch types. */}
            <div className="oe-checkbox-grid">
              <div className="oe-checkbox-column">
                {/* Common (Hidden Question / Quota Question moved up next to
                    Required in the Settings box's top checkbox row) */}
                <CheckboxWithTooltip
                  checked={oeParameters?.isSamePage || false}
                  onChange={(e) => handleChange('isSamePage', e.target.checked)}
                  label="Display on Same Page"
                  tooltip="Keep this question on the same page as the immediately preceding question. To group 3+ questions together, enable this on each consecutive question in the group."
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isReadOnly || false}
                  onChange={(e) => handleChange('isReadOnly', e.target.checked)}
                  label="Read Only Question"
                  tooltip="Display this question as read-only - respondents cannot change its value"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.isNoTranslation || false}
                  onChange={(e) => handleChange('isNoTranslation', e.target.checked)}
                  label="Exclude from Translator"
                  tooltip="No Translation required for this Question"
                />
                {isPropVisible('isMaxDiff') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isMaxDiff || false}
                    onChange={handleMaxDiffChange}
                    label="Max Diff Setup"
                    tooltip="Enable MaxDiff with balanced design or custom design import"
                  />
                )}
                {isPropVisible('isWithoutMaxDiff') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isWithoutMaxDiff || false}
                    onChange={(e) => handleChange('isWithoutMaxDiff', e.target.checked)}
                    label="Max Diff without Setup"
                    tooltip="Enable MaxDiff without excel setup"
                  />
                )}

                {/* Specific to the current Question Type - all remaining
                    checkboxes in this column render contiguously below;
                    fields with their own inputs (Response Group, No Answer
                    Label) live in the "Additional Configuration" section
                    further down instead of interrupting the checkbox list. */}
                {columnASpecificVisible && (
                  <div className="oe-section-divider">Specific to this Question Type</div>
                )}
                {isPropVisible('isNoanswer') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isNoanswer || false}
                    onChange={(e) => handleChange('isNoanswer', e.target.checked)}
                    label="No Answer for Decipher"
                    tooltip="This will append a No Answer row/button for Decipher programming"
                  />
                )}
                {isPropVisible('isRightLeft') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isRightLeft || false}
                    onChange={(e) => handleChange('isRightLeft', e.target.checked)}
                    label="Right Left Setup"
                    tooltip="This will make Right Left Combination of rows and columns along with scale provided in Columns reverse statement in Statements. Only applicable in Grids"
                  />
                )}
                {isPropVisible('isLoop') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isLoop || false}
                    onChange={(e) => handleChange('isLoop', e.target.checked)}
                    label="Run Rows as Loop"
                    tooltip="Render each grid row as its own looped block/page instead of a single grid page. Only applicable in Grids"
                  />
                )}
                {isPropVisible('isUnbound') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isUnbound || false}
                    onChange={(e) => handleChange('isUnbound', e.target.checked)}
                    label="Show Unbound"
                    tooltip="Export this String Grid as Dimension's UnboundList question look (free-text boxes) instead of the standard grid layout"
                  />
                )}
                {/* Carousel/Gridbars/CardSort/Accordion (+ their sub-options)
                    are independent checkboxes only for types with no Mode
                    selector, or whose Mode list doesn't happen to claim that
                    specific flag. Where a type's Mode dropdown does own the
                    flag (e.g. SingleGrid/MultiGrid), it's picked there
                    instead - hidden here entirely, not just unchecked. */}
                {isPropVisible('isCarousel') && !isModeOwned('isCarousel') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isCarousel || false}
                    onChange={(e) => handleChange('isCarousel', e.target.checked)}
                    label="Carousel Setup"
                    tooltip="This will make Carousel Setup"
                  />
                )}
                {isPropVisible('isCarouselGridbars') && !isModeOwned('isCarouselGridbars') && oeParameters?.isCarousel && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isCarouselGridbars || false}
                    onChange={(e) => handleChange('isCarouselGridbars', e.target.checked)}
                    label="Carousel Gridbars"
                    tooltip="Show gridbars within the carousel layout"
                  />
                )}
                {isPropVisible('isGridbars') && !isModeOwned('isGridbars') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isGridbars || false}
                    onChange={(e) => handleChange('isGridbars', e.target.checked)}
                    label="Gridbars"
                    tooltip="Display grid rows/columns as bars"
                  />
                )}
                {isPropVisible('isCardSort') && !isModeOwned('isCardSort') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isCardSort || false}
                    onChange={(e) => handleChange('isCardSort', e.target.checked)}
                    label="CardSort"
                    tooltip="Display the grid as a card-sort exercise"
                  />
                )}
                {isPropVisible('isAccordion') && !isModeOwned('isAccordion') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isAccordion || false}
                    onChange={(e) => handleChange('isAccordion', e.target.checked)}
                    label="Accordion"
                    tooltip="Display grid rows as a collapsible accordion"
                  />
                )}
                {isPropVisible('isAccordionAnswerButton') && !isModeOwned('isAccordionAnswerButton') && oeParameters?.isAccordion && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isAccordionAnswerButton || false}
                    onChange={(e) => handleChange('isAccordionAnswerButton', e.target.checked)}
                    label="Accordion Answer Button"
                    tooltip="Show answer options as buttons within the accordion"
                  />
                )}
                {isPropVisible('isCol') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isCol || false}
                    onChange={(e) => handleChange('isCol', e.target.checked)}
                    label="Column Layout"
                    tooltip="Make grid as Column selection"
                  />
                )}
                {isPropVisible('isSinCol') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isSinCol || false}
                    onChange={(e) => handleChange('isSinCol', e.target.checked)}
                    label="Show Rows as Columns"
                    tooltip="Show single response as columns"
                  />
                )}
                {/* Slider/Vertical Slider/Horizontal Rating Scale/Answer Button
                    are independent checkboxes only for types with no Mode
                    selector (Grids). For SingleResponse/MultipleResponse
                    they're chosen exclusively via the Mode dropdown above,
                    so they're hidden here entirely - not just left unchecked. */}
                {isPropVisible('isSlider') && !isModeOwned('isSlider') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isSlider || false}
                    onChange={(e) => handleChange('isSlider', e.target.checked)}
                    label="Slider"
                    tooltip="Display input as a slider control for numeric values"
                  />
                )}
                {isPropVisible('isVerticalSlider') && !isModeOwned('isVerticalSlider') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isVerticalSlider || false}
                    onChange={(e) => handleChange('isVerticalSlider', e.target.checked)}
                    label="Vertical Slider"
                    tooltip="Display the slider control vertically instead of horizontally"
                  />
                )}
                {isPropVisible('isHorizontalRatingScale') && !isModeOwned('isHorizontalRatingScale') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isHorizontalRatingScale || false}
                    onChange={(e) => handleChange('isHorizontalRatingScale', e.target.checked)}
                    label="Horizontal Rating Scale"
                    tooltip="Display the question as a horizontal rating scale"
                  />
                )}
                {isPropVisible('isAnswerButton') && !isModeOwned('isAnswerButton') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.isAnswerButton || false}
                    onChange={(e) => handleChange('isAnswerButton', e.target.checked)}
                    label="Answer Button"
                    tooltip="Display answer options as buttons"
                  />
                )}
              </div>

              <div className="oe-checkbox-column">
                {/* Common */}
                <CheckboxWithTooltip
                  checked={oeParameters?.isPageBreak || false}
                  onChange={(e) => handleChange('isPageBreak', e.target.checked)}
                  label="Remove pagebreak after question"
                  tooltip="Remove suspend tag"
                />
                <CheckboxWithTooltip
                  checked={oeParameters?.mainFolder || false}
                  onChange={(e) => handleChange('mainFolder', e.target.checked)}
                  label="Main Folder Started"
                  tooltip="This will place all question after this in main folder in Confirmit"
                />

                {/* Specific to the current Question Type */}
                {columnBSpecificVisible && (
                  <div className="oe-section-divider">Specific to this Question Type</div>
                )}
                {showRankingGroup && (
                  <div className="mutually-exclusive-group">
                    <CheckboxWithTooltip
                      checked={oeParameters?.isDrag || false}
                      onChange={(e) => handleChange('isDrag', e.target.checked)}
                      label="Drag"
                      tooltip="Enable drag functionality for reordering items"
                    />

                    <CheckboxWithTooltip
                      checked={oeParameters?.isDragDrop || false}
                      onChange={(e) => handleChange('isDragDrop', e.target.checked)}
                      label="Drag and Drop"
                      tooltip="Enable drag and drop functionality for reordering items"
                    />

                    {/* Same isDropDown flag as the Mode selector above - for
                        types whose Mode list owns it, it's chosen there
                        instead, so it's not duplicated in this ranking group. */}
                    {isPropVisible('isDropDown') && !isModeOwned('isDropDown') && (
                      <CheckboxWithTooltip
                        checked={oeParameters?.isDropDown || false}
                        onChange={(e) => handleChange('isDropDown', e.target.checked)}
                        label="Drop Down"
                        tooltip="Enable Drop down functionality for answers"
                      />
                    )}
                    <CheckboxWithTooltip
                      checked={oeParameters?.isClick || false}
                      onChange={(e) => handleChange('isClick', e.target.checked)}
                      label="Click to Rank"
                      tooltip="Enable Clickable design for Rank Functionality in Confirmit"
                    />
                    <CheckboxWithTooltip
                      checked={oeParameters?.isNumberRank || false}
                      onChange={(e) => handleChange('isNumberRank', e.target.checked)}
                      label="Number to Rank"
                      tooltip="Enable Number design for Rank Functionality"
                    />
                  </div>
                )}

                {isPropVisible('istablelook') && (
                  <CheckboxWithTooltip
                    checked={oeParameters?.istablelook || false}
                    onChange={(e) => handleChange('istablelook', e.target.checked)}
                    label="Show Numeric Question as Table"
                    tooltip="This will show the numeric question in table format"
                  />
                )}
              </div>
            </div>

            {isPropVisible('noAnswerLabel') && oeParameters?.isNoanswer && (
              <div className="oe-config-block">
                <Form.Group className="oe-inline-text-field">
                  <Form.Label className="oe-form-label">No Answer Button Label</Form.Label>
                  <Form.Control
                    type="text"
                    value={oeParameters?.noAnswerLabel || ""}
                    onChange={(e) => handleChange('noAnswerLabel', e.target.value)}
                    placeholder="e.g. No Answer"
                    className="oe-form-control-sm"
                  />
                </Form.Group>
              </div>
            )}

            {isPropVisible('isMaxDiff') && oeParameters?.isMaxDiff && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowMaxDiffModal(true)}
                className="mt-2"
              >
                <FontAwesomeIcon icon={faTable} className="me-1" />
                Configure MaxDiff Design
              </Button>
            )}

            {isPropVisible('isMaxDiff') && oeParameters?.isMaxDiff && oeParameters?.maxDiffDesign && (
              <div className="maxdiff-summary mt-2 p-2 bg-light rounded">
                <small>
                  <strong>MaxDiff Design:</strong><br />
                  Method: {oeParameters.maxDiffDesign.designMethod}<br />
                  Versions: {oeParameters.maxDiffDesign.actualVersions || oeParameters.maxDiffDesign.designVersions}<br />
                  Tasks per version: {oeParameters.maxDiffDesign.actualTasksPerVersion || oeParameters.maxDiffDesign.tasksPerRespondent}<br />
                  Items per set: {oeParameters.maxDiffDesign.actualItemsPerSet || oeParameters.maxDiffDesign.itemsPerSet}<br />
                  {oeParameters.maxDiffDesign.uniqueItemsUsed && (
                    <>Unique items: {oeParameters.maxDiffDesign.uniqueItemsUsed}<br /></>
                  )}
                  {oeParameters.maxDiffDesign.designMethod === 'pasted' && (
                    <span className="text-success">✓ Auto-detected from paste</span>
                  )}
                </small>
              </div>
            )}

            {isPropVisible('isMaxDiff') && (
              <MaxDiffConfigComponent
                oeParameters={oeParameters}
                setOeParameters={setOeParameters}
                onConfigUpdate={handleMaxDiffConfigUpdate}
                handleChange={handleChange}
                showMaxDiffModal={showMaxDiffModal}
                setShowMaxDiffModal={setShowMaxDiffModal}
                maxDiffDesign={maxDiffDesign}
                setMaxDiffDesign={setMaxDiffDesign}
              />
            )}
          </div>

          {/* Validate */}
          <div className="oe-settings-box">
            <h5>Validate</h5>
            <Form.Group className="oe-form-label-margin-bottom">
              <Form.Label className="oe-form-label">Validation logic</Form.Label>
              <FormattableInput
                value={oeParameters?.validationLogic || ""}
                onChange={(e) => handleChange('validationLogic', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Validation logic"
              />
              <Form.Label className="oe-form-label">Error Message</Form.Label>
              <FormattableInput
                value={oeParameters?.errorMessage || ""}
                onChange={(e) => handleChange('errorMessage', e.target.value)}
                type="contentEditable"
                className="oe-formattable-input"
                placeholder="Error Message if any"
              />
            </Form.Group>

            <div className="oe-validate-checkboxes">
              {/* Common */}
              {isPropVisible('isSequential') && (
                <CheckboxWithTooltip
                  checked={oeParameters?.isSequential || false}
                  onChange={(e) => handleChange('isSequential', e.target.checked)}
                  label="Sequential"
                  tooltip="Ensure responses are entered in sequential order"
                />
              )}
              <Form.Group className="oe-form-label-margin-bottom">
                <Form.Label className="oe-form-label">Parameters</Form.Label>
                <FormattableInput
                  value={oeParameters?.parameters || ""}
                  onChange={(e) => handleChange('parameters', e.target.value)}
                  type="contentEditable"
                  className="oe-formattable-input"
                  placeholder="Additional parameters for this question"
                />
              </Form.Group>

              {/* Specific to the current Question Type */}
              {validateSpecificVisible && (
                <div className="oe-section-divider">Specific to this Question Type</div>
              )}
              {isPropVisible('isFirstComp') && (
                <CheckboxWithTooltip
                  checked={oeParameters?.isFirstComp || false}
                  onChange={(e) => handleChange('isFirstComp', e.target.checked)}
                  label="First Box Compulsory"
                  tooltip="Ensure First Box is compulsory in Open end"
                />
              )}
              {isPropVisible('isDuplicate') && (
                <CheckboxWithTooltip
                  checked={oeParameters?.isDuplicate || false}
                  onChange={(e) => handleChange('isDuplicate', e.target.checked)}
                  label="Duplicate Entries Check"
                  tooltip="Ensure to check the duplicate entries in Open end"
                />
              )}
              {isPropVisible('disabledCells') && (
                <Form.Group className="oe-form-label-margin-bottom">
                  <Form.Label className="oe-form-label">Disable cells</Form.Label>
                  <FormattableInput
                    value={oeParameters?.disabledCells || ""}
                    onChange={(e) => handleChange('disabledCells', e.target.value)}
                    type="contentEditable"
                    className="oe-formattable-input"
                    placeholder="Disable Cells: R1C1, R2C3 R1 C4 etc"
                  />
                </Form.Group>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* {JSON.stringify(selectedVariable)} */}
    </div>
  );
};

export default OESettingsComponent;